import FieldPromote from "./field-promote";
import Field from "./field";
import Entity from "./entity";
import Api from "./api";
import { replaceAll } from "../utils";
import IRelation from "../interface/relation";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IApiMasterDetail from "../interface/api/api-master-detail";
import IApiField from "../interface/api/api-field";
import Global from "../global";
import ErrorCode from "../enum/error-code";

class RelationSingleton {

    /* Public Methods */

    public getAll(entityId: number): IRelation[] {
        return this.getAllRelationsTo().filter((relation: IRelation) => relation.ecnTo === entityId);
    }

    public getSiblings(relationId: number): IRelation[] {
        const all: IRelation[] = this.getAllRelationsTo();
        const rel: IRelation | null = all.find((r: IRelation) => r.id === relationId) || null;
        if (rel) {
            return all.filter((r: IRelation) => r.ecnTo === rel.ecnTo);
        }
        return [];
    }

    public async create(fromEntityId: number, toEntityId: number, verb: string = '', singular: string = ''): Promise<IRelation> {
        const to: IEntity | null = Entity.get(toEntityId);
        if (to) {
            if (fromEntityId === -1) {
                const entity: IEntity = await Entity.create(singular.trim());
                fromEntityId = entity.id;
            }
            const from: IEntity | null = Entity.get(fromEntityId);
            if (from) {
                const field: IField = await Field.create(from.id, to.singular);
                const linked: IField = await FieldPromote.toLinked(field.id, {
                    entityId: to.id,
                    verbTo: verb || Global.REL_SUB_DETAIL
                });
                return this.getRelationTo(linked.linkedRelation[0]) as IRelation;
            } else {
                throw ErrorCode.BadArguments;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async rename(id: number, verb: string = Global.REL_SUB_DETAIL): Promise<IRelation> {
        const field: IApiField | null = Api.getField(id);
        if (verb && field && field.pointer) {
            field.pointer[0].ptVerb = verb;
            await Api.fieldSave(field);
            return this.getRelationTo(id) as IRelation;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    /* Private Public Methods */

    public getRelationTo(id: number): IRelation | null {
        return this.getAllRelationsTo().find((relation: IRelation) => relation.id === id) || null;
    }

    /* Private Methods */

    private getAllRelationsTo(): IRelation[] {
        const result: IRelation[] = [];
        Api.buildMasterDetailDico().forEach((item: IApiMasterDetail) => {
            const relation: IRelation = {
                id: item.relationField,
                ecnFrom: item.detailTable,
                ecnTo: item.masterTable,
                verb: item.relationVerb,
                name: item.relationVerb
            };
            relation.name = replaceAll(relation.name, Global.REL_SUB_DETAIL, unescape(item.detailSingular));
            relation.name = replaceAll(relation.name, Global.REL_SUB_MASTER, unescape(item.masterSingular));
            result.push(relation);
        });
        const entities = Entity.getAll();
        return result.filter(r => {
            const from = entities.find(e => e.id === r.ecnFrom);
            const to = entities.find(e => e.id === r.ecnTo);
            return from && to;
        });
    }

}

const Relation: RelationSingleton = new RelationSingleton();

export default Relation;